import { transparentize } from 'polished';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { getAppParameters } from 'src/AppParameters';
import {
	EasyClaimAppState,
	EasyClaimDocumentType,
	DocumentFeature,
	ValidationResult,
} from 'src/easyclaim.api.client.typescript/src/models';
import { ExclamationSimple } from 'src/easyclaim.app.assets/icons/ExclamationSimple';
import styled from 'styled-components';

import { FeedbackIcon } from '..';
import { messages } from '../../easyclaim.app.internationalization/index';
import { Image } from '../../easyclaim.app.services/index';

import FormattedHtmlMessage from './FormattedHtmlMessage';
import { getAdditionalFeedback } from './ImageRecognitionAdditionalFeedback';
import { pictureRejectionDocumentFeatures } from './PictureRejection';

export interface ImageRecognitionFeedbackPhotosInsteadWarningProps {
	image?: Image;
	onShowExample?: () => void;
	feedbackMode: FeedbackMode;
	screen: EasyClaimAppState;
}

export enum FeedbackMode {
	Main,
	Modal,
}

export class ImageRecognitionFeedbackPhotosInsteadWarning extends React.Component<
	ImageRecognitionFeedbackPhotosInsteadWarningProps,
	{}
> {
	render() {
		const treatErrorAsWarning =
			this.props.image &&
			this.props.image?.uploadResult?.validationResults &&
			this.props.image?.uploadResult?.validationResults.filter(
				(vr) => vr.validationResult === ValidationResult.Error
			) &&
			this.props.image?.uploadResult?.validationResults.filter((vr) =>
				pictureRejectionDocumentFeatures.some((e) => e == vr.feature)
			);

		const hasWarnings =
			!!this.props.image &&
			this.props.image?.uploadResult?.validationResults &&
			this.props.image?.uploadResult?.validationResults.filter(
				(vr) => vr.validationResult === ValidationResult.Warning
			).length > 0;

		const featuresWithExemplaryPhoto = [
			DocumentFeature.ExpectedNumberPlate,
			DocumentFeature.ExpectedFrontLeftPerspective,
			DocumentFeature.ExpectedFrontRightPerspective,
			DocumentFeature.ExpectedRearLeftPerspective,
			DocumentFeature.ExpectedRearRightPerspective,
			DocumentFeature.UndesiredPhotoOfScreen,
		];

		const hasErrorsWithExemplaryPhoto =
			!!this.props.image &&
			this.props.image?.uploadResult?.validationResults &&
			this.props.image?.uploadResult?.validationResults.filter(
				(vr) =>
					vr.validationResult === ValidationResult.Error &&
					featuresWithExemplaryPhoto.some((e) => e == vr.feature)
			).length > 0;

		const validationResults =
			!!this.props.image && !!this.props.image?.uploadResult?.validationResults
				? this.props.image?.uploadResult?.validationResults
				: null;

		const appParameters = getAppParameters();
		const lang = appParameters.lang;
		const additionalFeedbackTranslationId = getAdditionalFeedback(
			this.props.feedbackMode,
			validationResults
		);
		const additionalInfoTranslationExists =
			!!messages && !!messages[lang][additionalFeedbackTranslationId];

		const stateLowercase = this.props.screen.charAt(0).toLowerCase() + this.props.screen.slice(1);
		const translationId = `${stateLowercase}.${MapDocumentTypeToString(this.props.image?.document?.documentType)}`;

		const imageRecognitionFeedbacks = (
			<ImageRecognitionFeedbacks className="feedback-main-container">
				<FeedbackContainer className="feedback-container">
					<Feedback>
						<FormattedMessage id={translationId} />
					</Feedback>
				</FeedbackContainer>
				{<ExclamationSimple className="feedback-mismatch-icon" />}
			</ImageRecognitionFeedbacks>
		);

		const imageRecognitionFeedbacksInfoTexts =
			validationResults &&
			validationResults
				.filter((vr) => !!vr.description)
				.map((key, index) => (
					<InfoText
						key={index}
						className="ImageInfoContainer-FeedbackText"
						dangerouslySetInnerHTML={{ __html: key.description! }}
					/>
				));

		return !!this.props.image && validationResults && validationResults.length > 0 ? (
			<ImageInfoContainer className="ImageInfoContainer">
				{imageRecognitionFeedbacks}
				{imageRecognitionFeedbacksInfoTexts}
				{hasWarnings && additionalInfoTranslationExists && (
					<div
						className="additional-feedback"
						style={{
							fontWeight: 'lighter',
							fontStyle: 'italic',
							marginBottom: '1rem',
							marginTop: '1rem',
						}}
					>
						<FormattedHtmlMessage id={additionalFeedbackTranslationId} />
					</div>
				)}
				{this.props.onShowExample && (hasWarnings || hasErrorsWithExemplaryPhoto) && (
					<ActionsContainer>
						<Action
							className="ImageInfoContainer-exemplaryPhoto"
							onClick={this.props.onShowExample}
						>
							<FormattedMessage id="imageRecognitionFeedback2.exemplaryPhoto" />
						</Action>
					</ActionsContainer>
				)}
			</ImageInfoContainer>
		) : null;
	}
}

const ActionsContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	flex-wrap: wrap;

	& > :not(:first-child) {
		margin-left: 2rem;
	}
`;

const Action = styled.a`
	color: ${(p) => p.theme.colors.primary};
	text-transform: uppercase;
	cursor: pointer;
	text-decoration: none;
	user-select: none;
	background-image: none;
	font-weight: bold;
	font-size: 0.85rem;
`;

const InfoText = styled.div`
	font-weight: lighter;
	font-style: italic;

	margin-top: 1rem;
	margin-bottom: 1rem;
`;

const Feedback = styled.span`
	font-size: 0.9rem;
	font-weight: bolder;
`;
const ImageRecognitionFeedbacks = styled.div``;

const FeedbackContainer = styled.div`
	position: relative;
`;

const ImageInfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	flex-grow: 0;
	padding: 1rem;
	border-radius: 0px 0px 3px 3px;
	background-color: ${(p) => transparentize(0.9, p.theme.colors.secondary)};
`;

function MapDocumentTypeToString(documentType?: EasyClaimDocumentType): string {
	switch (documentType) {
		case EasyClaimDocumentType.FrontLeftPicture:
			return 'vehicleFrontLeft.title';
		case EasyClaimDocumentType.FrontRightPicture:
			return 'vehicleFrontRight.title';
		case EasyClaimDocumentType.RearLeftPicture:
			return 'vehicleRearLeft.title';
		case EasyClaimDocumentType.RearRightPicture:
			return 'vehicleRearRight.title';
		case EasyClaimDocumentType.OverallDashboardPicture:
			return 'overallDashboard.title';
		case EasyClaimDocumentType.DashboardPicture:
			return 'mileage.title';
		case EasyClaimDocumentType.WindshieldPicture:
			return 'windshield.title';
		case EasyClaimDocumentType.VinPicture:
			return 'vin.title';
		case EasyClaimDocumentType.DetailPicture:
			return 'optionalDamageImages';
		default:
			return '';
	}
}
